import { useTranslations } from '@vocab/react';
import { Step, Stepper } from 'braid-design-system';
import React from 'react';
import { useDispatch } from 'react-redux';

import {
  CAREER_HISTORY,
  EDUCATION,
  FINISH_RESUME,
  LANGUAGES,
  LICENCES,
  PERSONAL_DETAILS,
  SKILLS,
  START_RESUME,
  SUMMARY,
} from 'src/components/Router';
import { updateRoute } from 'src/components/Router/actionCreators';
import { READ } from 'src/consts';
import { useAppConfig } from 'src/modules/appConfig';
import { useTypedSelector } from 'src/modules/hooks/useTypedSelector';
import type { Card } from 'src/types';

import { useIsValid } from '../../Validation/Validation';
import { updateActiveRoute } from '../../actionCreators';
import type { CreateResumeRoute } from '../../types';

import translations from './.vocab';

interface Props {
  headerHeight: number | 'auto';
}

const mapStepToNumber: Record<string, number> = {
  [START_RESUME]: 0,
  [PERSONAL_DETAILS]: 1,
  [CAREER_HISTORY]: 2,
  [EDUCATION]: 3,
  [LICENCES]: 4,
  [SKILLS]: 5,
  [LANGUAGES]: 6,
  [SUMMARY]: 7,
  [FINISH_RESUME]: 8,
};

const getActiveStep = ({
  step,
  showPersonalSummary,
}: {
  step?: CreateResumeRoute;
  showPersonalSummary: boolean;
}) => {
  if (!step) {
    return 0;
  }
  if (!showPersonalSummary) {
    if (step === FINISH_RESUME) {
      return 7;
    }
  }
  return mapStepToNumber[step];
};

export const ProgressMarkerContainer = ({ headerHeight }: Props) => {
  const { t } = useTranslations(translations);
  const { showFeature } = useAppConfig();
  const showPersonalSummary = showFeature('personal-summary');

  const dispatch = useDispatch();
  const inView = useTypedSelector((state) => state.createResume.inView);
  const checkStepValidity = useIsValid();

  const onStepClick = ({ id }: { id?: string | number }) => {
    dispatch(
      updateRoute({
        to: id as Card,
        mode: READ,
        params: {
          offset: typeof headerHeight === 'number' && headerHeight + 30,
          allowScroll: true,
        },
      }),
    );
    dispatch(
      updateActiveRoute({ route: id as CreateResumeRoute, source: 'header' }),
    );
  };

  const activeStep = getActiveStep({ step: inView, showPersonalSummary });

  return showPersonalSummary ? (
    <Stepper
      label="Linear steps"
      mode="non-linear"
      align="left"
      activeStep={activeStep}
      onStepClick={onStepClick}
      data={{ automation: 'create-resume-stepper' }}
    >
      <Step
        id="personal-details"
        complete={checkStepValidity('personal-details')}
      >
        {t('Personal details')}
      </Step>
      <Step id="career-history" complete={checkStepValidity('career-history')}>
        {t('Career history')}
      </Step>
      <Step id="education" complete={checkStepValidity('education')}>
        {t('Education')}
      </Step>
      <Step id="licences" complete={checkStepValidity('licences')}>
        {t('Licences')}
      </Step>
      <Step id="skills" complete={checkStepValidity('skills')}>
        {t('Skills')}
      </Step>
      <Step id="languages" complete={checkStepValidity('languages')}>
        {t('Languages')}
      </Step>

      <Step id="summary" complete={checkStepValidity('summary')}>
        {t('Personal summary')}
      </Step>
      <Step id="finish-resume">{t('Done')}</Step>
    </Stepper>
  ) : (
    <Stepper
      label="Linear steps"
      mode="non-linear"
      activeStep={activeStep}
      onStepClick={onStepClick}
    >
      <Step
        id="personal-details"
        complete={checkStepValidity('personal-details')}
      >
        {t('Personal details')}
      </Step>
      <Step id="career-history" complete={checkStepValidity('career-history')}>
        {t('Career history')}
      </Step>
      <Step id="education" complete={checkStepValidity('education')}>
        {t('Education')}
      </Step>
      <Step id="licences" complete={checkStepValidity('licences')}>
        {t('Licences')}
      </Step>
      <Step id="skills" complete={checkStepValidity('skills')}>
        {t('Skills')}
      </Step>
      <Step id="languages" complete={checkStepValidity('languages')}>
        {t('Languages')}
      </Step>
      <Step id="finish-resume">{t('Done')}</Step>
    </Stepper>
  );
};
